// "Print previously purchased" (2026-09-03, Michael's staff request).
//
// A customer buys a print, inspects it, then comes back for framing. Staff
// build the framing in the visualizer (all the automated specs) and tick this
// box: the PRINT component is not charged, only the framing/mat/mounting,
// and the source order is named so production pulls the existing print.
//
// Price math is client-side only. /prisma/pricing/sizes already returns a
// per-size breakdown ({paperPrice, treatmentPrice, ...}) as the 4th element
// of every row — the visualizer just never used it. We record paperPrice by
// size here (from every pricing response, see pricing.api.ts) and subtract
// it from currPricing when the box is ticked. Checkout charges the stored
// itemJson.price as-is (2026-08-26 rule), so the reduced price flows through.
//
// Admin-only UI: the control renders only when the session is an admin.
import { atom } from "nanostores";
import type { VisualizerStoreProps } from "./visualizer";
import { PRODUCT_TYPE } from "@/utils/content/productTypes";
import { $cart } from "./cart";
import { MULTI_WINDOW_EXTRA, isMultiWindow, multiWindowPrints } from "./multiWindow";

export const PREV_PRINTED_LABEL = "Suppl'd, prvsly printed";

// "Customer supplied art" (2026-09-03, Michael): staff quote framing for art
// the customer hands us — no digital file. Price is NOT reduced: a supplied
// piece is charged like a Tier-1 print of that size + the framing (pick any
// Tier-1 paper; the quote tool's "Provided Art" is priced the same way). The
// line replaces the paper name with this label everywhere (cart, checkout,
// confirmation, packing slip, work order), stamps itemJson.supplied so the
// masterlist reads "suppl'd prnt" (no paper), and the work order prints a
// remeasure warning for the fitter / woodshop. Mutually exclusive with
// "previously purchased".
export const SUPPLIED_ART_LABEL = "Customer supplied art";
/** The paper-selection line for a supplied piece: "Customer supplied art - red square" (Michael 09-22). */
export const suppliedArtLabel = (title?: string): string => {
  const t = String(title || "").trim();
  return t ? `${SUPPLIED_ART_LABEL} - ${t}` : SUPPLIED_ART_LABEL;
};
export function isSuppliedArtEligible(store: VisualizerStoreProps): boolean {
  return isPrevPrintedEligible(store);
}

// "WxH" (3-dp) → paperPrice, from the latest pricing responses.
export const $paperPriceBySize = atom<Record<string, number>>({});

const sizeKey = (w: number, h: number) =>
  `${Number(w).toFixed(3)}x${Number(h).toFixed(3)}`;

/** Record paperPrice per size from a /prisma/pricing/sizes response. */
export function recordPaperPrices(rows: any[] | undefined | null): void {
  if (!Array.isArray(rows) || !rows.length) return;
  const next = { ...$paperPriceBySize.get() };
  let changed = false;
  for (const row of rows) {
    const dims = row?.[0];
    const breakdown = row?.[3];
    if (!Array.isArray(dims) || !breakdown) continue;
    const w = Number(dims[0]);
    const h = Number(dims[1]);
    const pp = Number(breakdown.paperPrice);
    if (!(w > 0) || !(h > 0) || !Number.isFinite(pp)) continue;
    const k = sizeKey(w, h);
    if (next[k] !== pp) {
      next[k] = pp;
      changed = true;
    }
  }
  if (changed) $paperPriceBySize.set(next);
}

/** paperPrice for a size (either orientation); undefined when unknown. */
export function getPaperPrice(w: number, h: number): number | undefined {
  const m = $paperPriceBySize.get();
  return m[sizeKey(w, h)] ?? m[sizeKey(h, w)];
}

/** Giclée with a framing / mat treatment — the only case with a print component to waive. */
export function isPrevPrintedEligible(store: VisualizerStoreProps): boolean {
  if (store.selectedProduct !== PRODUCT_TYPE.GICLEE) return false;
  const t = String(store.options?.treatmentOptions?.type || "");
  return !!t && !/no frame/i.test(t);
}

const parseMoney = (s: unknown): number =>
  parseFloat(String(s ?? "").replace(/[$,\s]/g, "")) || 0;

/**
 * The price to show and to stamp on the line: full price minus the print
 * component when "previously purchased" is on and applicable.
 * Returns { display, value, waived } — display keeps currPricing's "$x.xx" shape.
 */
export function effectivePricing(store: VisualizerStoreProps): {
  display: string;
  value: number;
  waived: number;
  /** Box ticked on an eligible item (flag + note get stamped). */
  requested: boolean;
  /** Print component was known and actually deducted. */
  applied: boolean;
  /** Multi-window mat (stores/multiWindow.ts): windows ticked and the extra-window charge. */
  multi?: { windows: number; extra: number };
} {
  const full = parseMoney(store.currPricing);
  // Multi-window mat: no print on this line (the prints are their own lines) + $15 per extra window.
  if (isMultiWindow(store)) {
    const windows = multiWindowPrints(store, $cart.get().lineItems as any[]).length;
    const extra = Math.max(0, windows - 1) * MULTI_WINDOW_EXTRA;
    const multi = { windows, extra };
    const { width, height } = store.selectedSize?.dimensions || ({} as any);
    const pp = getPaperPrice(Number(width), Number(height));
    if (pp === undefined) return { display: store.currPricing, value: full, waived: 0, requested: true, applied: false, multi };
    const value = Math.max(0, Math.round((full - pp + extra) * 100) / 100);
    return { display: `$${value.toFixed(2)}`, value, waived: pp, requested: true, applied: true, multi };
  }
  const requested = !!store.prevPrinted && isPrevPrintedEligible(store);
  const base = { display: store.currPricing, value: full, waived: 0, requested, applied: false };
  if (!requested) return base;
  const { width, height } = store.selectedSize?.dimensions || ({} as any);
  const pp = getPaperPrice(Number(width), Number(height));
  // Unknown print component for this size — never guess a discount.
  if (pp === undefined) return base;
  const value = Math.max(0, Math.round((full - pp) * 100) / 100);
  return { display: `$${value.toFixed(2)}`, value, waived: pp, requested, applied: true };
}

export function prevPrintedNote(order?: string | null): string {
  const o = String(order || "").trim();
  return o ? `${PREV_PRINTED_LABEL}: ${o}` : PREV_PRINTED_LABEL;
}
